var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { CATALOG, NON_DELEGATABLE } from '../authz';
import { instanceKey, specForKey, splitInstance } from '@vitesse/trust';
import { chip, pillTabBar, statusBadge } from '../components/templates';
import { choiceFor, grantFor, flatten } from '../org-members';
import './vitesse-pill-tabs';
import './vitesse-icon';
import { memberAvatar } from './member-avatar';
import { getSession } from '../session';
import './vitesse-gate-overlay';
/*
 * <vitesse-rbac-panel> — who in this org may do what, and on which surface.
 *
 * ONE PANEL, EVERY UI, MOUNTED WHERE PEOPLE ALREADY MANAGE THEIR ORG. It is a
 * custom page inside Clerk's OrganizationProfile modal, beside General and
 * Members — the same place an admin already goes to add a colleague — rather than
 * a page in one app that the other three never show. The page object itself is
 * built once in org-rbac-page.ts and passed to Clerk as a customPage.
 *
 * LIGHT DOM, DELIBERATELY. It renders into the host document and uses the shared
 * `.base-panel` / `.base-field` / `.base-list-row` classes, so a row here is the
 * same object as a row in the AI console's MCP list — not a lookalike. The first
 * cut used a shadow root, which cannot inherit those classes, so every one of
 * them had to be restated: two hundred lines that LOOKED like the design system
 * and would drift out of it the first time the real one changed.
 *
 * IT FETCHES NOTHING. Members and roles arrive as properties (Cardinal Rule);
 * every write is a callback the host supplies, because the writes go to
 * FleetControl and to the identity provider, and this package must know neither.
 *
 * TWO ROLE SYSTEMS, AND THEY ARE NOT THE SAME QUESTION:
 *
 *   IdP org role (Admin / Member)    — governs the ORG: who may invite, who may
 *     open this editor at all. It also sets the permission BASELINE, which is why
 *     a row can read "Default — admin only".
 *   Vitesse permission profile        — a named bundle layered ON TOP of that
 *     baseline, defined per org because the IdP's own custom roles are
 *     instance-wide and capped.
 *
 * They were shown as one column and a subtitle, which read as though the second
 * had been bolted onto the first. Both are first-class here: two labelled
 * controls on the same row, each writing to the system that owns it.
 *
 * THE PERMISSION MODEL, and why the two "empty" states are not the same:
 *
 *   absent key   → DEFAULT. No opinion authored; the baseline or profile decides.
 *   empty array  → REVOKED. Authored, and authored to nothing — the only way to
 *                  take back what a profile grants.
 *   ['*']        → every surface.
 *   ['app']      → that surface only. The second axis: "may write symbols from
 *                  the app, but not through the AI acting on their behalf."
 *
 * Collapse absent into empty and Save silently restores everything an admin
 * revoked, on exactly the people they revoked it from, with the screen identical
 * before and after. org-members.ts carries the distinction and is tested for it.
 */
/*
 * THE TWO BUILT-IN ROLES, as entries in the SAME list as the custom profiles.
 *
 * The IdP's org role and a Vitesse profile are two stores, and the first version of
 * this screen showed them as two controls — which put "Admin" and "Operator" on
 * one person and asked the reader to work out how that composed. It composes
 * badly: AllowFrom consults the authored matrix BEFORE the role baseline, so a
 * profile laid over an admin does not add to them, it SUBTRACTS — an "Operator
 * who is also an Admin" is an admin with holes in them, which is never what
 * anyone picking a role meant. It had already silently restricted someone.
 *
 * So the seam is ours to hide, and one question gets one answer:
 *
 *   Admin     → org role admin, no profile, no matrix.
 *   Member    → org role member, no profile, no matrix.
 *   <profile> → org role member, plus that profile.
 *
 * A custom profile always sits on the member baseline, because that is the only
 * baseline it can meaningfully extend. What this gives up is "an admin, except
 * for one thing" — the incoherent quadrant — and the per-person Edit still
 * authors exactly that when someone genuinely wants it.
 *
 * The values are prefixed so a profile can never be named into collision with a
 * built-in: `Admin` as a profile name is a different string from BUILT_IN_ADMIN.
 */
const BUILT_IN_ADMIN = '\u0000admin';
const BUILT_IN_MEMBER = '\u0000member';
/*
 * PLAIN LANGUAGE, NOT THE KEY.
 *
 * A permission key is a wire contract — `symbol:annotate` is stored, sent and
 * enforced under that exact spelling — but it is not something to put in front
 * of the person deciding who may do what. Every row's name, one-line
 * description and section heading come from the locale table instead, so the UI
 * can say "Label values" while the contract keeps saying `symbol:annotate`, and
 * the same row reads correctly in every language.
 *
 * i18next treats ':' as a namespace separator, so the sub-key is the catalog key
 * with the colons swapped for underscores.
 *
 * `t` is typed against en's literal key union, which a key COMPUTED from a
 * permission cannot satisfy, so it is called through a string-keyed view of
 * itself. What that gives up, `test/permission-copy.test.ts` takes back: it
 * fails the build when any catalog entry lacks copy in any locale, which is the
 * check a literal union could not have made anyway — the union knows the keys
 * en happens to declare, not the ones the contract requires.
 */
const tk = t;
/** The action's name, e.g. `symbol:write` → "Change a value". */
export function permLabel(key) {
    return tk(`perm.label.${key.replace(/:/g, '_')}`);
}
/** What granting it lets a person do, in a few words. */
export function permDesc(key) {
    return tk(`perm.desc.${key.replace(/:/g, '_')}`);
}
/** What a Vitesse role is FOR, in one line. Keyed by name because the role's body
 *  is a permission matrix — accurate, and useless as a description: nobody reads
 *  sixteen grant keys to find out that a role is the background agent. A role with
 *  no entry falls back to its own name rather than rendering a raw key. */
export function roleHint(name) {
    const key = `perm.roleAbout.${name.replace(/[^A-Za-z0-9]+/g, '_')}`;
    const out = tk(key);
    return out === key ? name : out;
}
/** The section heading — plural, because a section holds several actions. */
export function permGroupLabel(group) {
    return tk(`perm.group.${group}`);
}
let VitesseRbacPanel = class VitesseRbacPanel extends LitElement {
    constructor() {
        super(...arguments);
        // Re-renders on a locale change; the controller has no value to read, it just
        // requests an update.
        this.#locale = new LocaleController(this);
        /** The org's members, supplied by the host. */
        this.members = [];
        /** The org's roles, read through ListOrgRoles by the host. */
        this.roles = [];
        /**
         * Whether this viewer may author a MEMBER's access (SetUserGrants).
         *
         * Two flags, because the two authoring surfaces are two RPCs selling two
         * different tiers — roles are `profiles`, the per-user matrix is `per_user`.
         * One flag standing for both would offer an org on the cheaper tier a Save its
         * server refuses, and hide one the server would have accepted.
         */
        this.canEdit = false;
        /** Whether this viewer may author ROLES (SetOrgRoles). */
        this.canEditRoles = false;
        /**
         * The org's specialists, so "delegate to a specialist" can name WHICH one.
         *
         * Injected rather than fetched: this panel renders in three apps and only one
         * of them talks to the AI plane. A host that has no list passes none, and the
         * permission renders as the single wildcard row it has always been — so the
         * screen degrades to the old behaviour instead of to an empty section.
         */
        this.specialists = [];
        this.tab = 'people';
        this.selected = '';
        this.draft = {};
        this.draftRole = '';
        this.dirty = false;
        this.saving = false;
        this.error = '';
        this.filter = '';
        /** Which permission GROUPS are expanded. Collapsed by default: fifty-nine rows
         *  is a wall, and the question an admin arrives with is almost always about one
         *  area. A search expands everything it matched. */
        this.openGroups = new Set();
        /** Which instance-scoped permissions are expanded. Separate from `openGroups`
         *  so opening a group does not also unfurl every specialist inside it — the
         *  group answers "what can be done here", the parent answers "to which one". */
        this.openPerms = new Set();
        /** Member-list search, separate from the permission filter — they are two lists
         *  and sharing one box would filter whichever you were not looking at. */
        this.memberFilter = '';
        /** Narrows the roles list, exactly as `memberFilter` narrows People — the same
         *  act on the neighbouring tab, so it works the same way. */
        this.roleFilter = '';
        // Roles tab. `roleDraft` is the whole set: SetOrgRoles replaces wholesale, so
        // editing one role and saving must send the others unchanged rather than
        // deleting them by omission.
        this.roleDraft = [];
        this.roleSel = '';
        this.roleDirty = false;
    }
    // Re-renders on a locale change; the controller has no value to read, it just
    // requests an update.
    #locale;
    /*
     * LIGHT DOM. The shared stylesheet lives in the host document, and a shadow
     * root would put every `.base-*` class out of reach — which is what forced the
     * first version to restate them.
     */
    createRenderRoot() {
        return this;
    }
    /* ── people tab ─────────────────────────────────────────────────────────── */
    select(m) {
        this.selected = m.userId;
        // structuredClone so a cancelled edit leaves the roster showing what the
        // server actually says, not what someone started typing.
        this.draftRole = m.assignedRole;
        this.draft = structuredClone(m.assignedRole ? m.overrides : m.grants);
        this.dirty = false;
        this.error = '';
    }
    /** The role currently assigned in the draft, if it still exists. */
    get assigned() {
        return this.roles.find((r) => r.name === this.draftRole);
    }
    setRole(name) {
        // Switching authoring shapes deliberately CLEARS the other one's rows rather
        // than reinterpreting them: a hand-authored matrix is not a set of deltas on
        // a role, and silently treating it as one would grant whatever the role adds
        // on top of everything the admin had already written.
        this.draftRole = name;
        this.draft = {};
        this.dirty = true;
    }
    setRow(key, choice) {
        const next = { ...this.draft };
        const value = grantFor(choice);
        if (value === undefined)
            delete next[key];
        else
            next[key] = value;
        this.draft = next;
        this.dirty = true;
    }
    async persist() {
        if (!this.save)
            return;
        this.saving = true;
        this.error = '';
        try {
            const role = this.draftRole;
            const payload = role
                ? { role, overrides: this.draft, grants: {} }
                : { role: '', overrides: {}, grants: this.draft };
            await this.save(this.selected, payload);
            const m = this.members.find((x) => x.userId === this.selected);
            if (m) {
                // Keep the roster in step, INCLUDING the flattened product — the chips and
                // a re-select both read it, and leaving it stale shows the previous role's
                // access on a member who no longer holds it.
                m.assignedRole = role;
                m.overrides = role ? structuredClone(this.draft) : {};
                m.grants = role ? flatten(this.assigned, this.draft) : structuredClone(this.draft);
            }
            this.dirty = false;
        }
        catch (e) {
            this.error = e instanceof Error ? e.message : String(e);
        }
        finally {
            this.saving = false;
        }
    }
    /* ── roles tab ──────────────────────────────────────────────────────────── */
    /*
     * The roles arrive AFTER mount — the panel is created, then the list is fetched
     * and assigned. `roleDraft` was cloned only on entering the tab, so whichever
     * order those two happened in decided what you saw: land on Roles before the
     * fetch resolves and the tab reads "no roles defined yet" for a org that has
     * them, with nothing to make it correct itself.
     *
     * So re-clone whenever the incoming list changes AND there is nothing unsaved to
     * lose. The dirty guard is the whole reason the draft exists — a late response
     * must never overwrite edits someone is in the middle of making — but with no
     * edits pending there is nothing to protect and a stale list is just wrong.
     */
    willUpdate(changed) {
        if (changed.has('roles') && !this.roleDirty)
            this.roleDraft = structuredClone(this.roles);
    }
    enterRoles() {
        this.tab = 'roles';
        this.roleDraft = structuredClone(this.roles);
        this.roleSel = '';
        this.roleDirty = false;
        this.error = '';
    }
    get roleBeingEdited() {
        return this.roleDraft.find((r) => r.name === this.roleSel);
    }
    addRole() {
        // A unique placeholder, because two roles with one name is a set with one
        // role in it and the second author's work vanishes on save.
        let name = t('perm.newRole');
        for (let n = 2; this.roleDraft.some((r) => r.name === name); n++)
            name = `${t('perm.newRole')} ${n}`;
        this.roleDraft = [...this.roleDraft, { name, grants: {} }];
        this.roleDirty = true;
        this.openRole(name);
    }
    renameRole(next) {
        const role = this.roleBeingEdited;
        if (!role)
            return;
        this.error = '';
        if (!next.trim()) {
            this.error = t('perm.emptyName');
            return;
        }
        if (this.roleDraft.some((r) => r !== role && r.name === next)) {
            this.error = t('perm.duplicateRole');
            return;
        }
        role.name = next;
        this.roleSel = next;
        this.roleDraft = [...this.roleDraft];
        this.roleDirty = true;
    }
    deleteRole() {
        this.removeRole(this.roleSel);
        this.roleSel = '';
    }
    /**
     * Drop one role from the draft, by name.
     *
     * Split out of `deleteRole` so the LIST can offer the same act the editor does, through the same
     * code — a second filter written at the row would be a second answer to "what does deleting a
     * role mean", and the two would drift the first time one of them learned about holders.
     *
     * Still a DRAFT edit: nothing leaves the browser until Save, which is what the whole panel does
     * and what makes an accidental click on a red button recoverable by Cancel.
     */
    removeRole(name) {
        if (!name)
            return;
        this.roleDraft = this.roleDraft.filter((r) => r.name !== name);
        this.roleDirty = true;
    }
    setRoleRow(key, choice) {
        const role = this.roleBeingEdited;
        if (!role)
            return;
        const value = grantFor(choice);
        if (value === undefined)
            delete role.grants[key];
        else
            role.grants[key] = value;
        this.roleDraft = [...this.roleDraft];
        this.roleDirty = true;
    }
    async persistRoles() {
        if (!this.saveRoles)
            return;
        this.saving = true;
        this.error = '';
        try {
            await this.saveRoles(this.roleDraft);
            this.roles = structuredClone(this.roleDraft);
            this.roleDirty = false;
        }
        catch (e) {
            this.error = e instanceof Error ? e.message : String(e);
        }
        finally {
            this.saving = false;
        }
    }
    /* ── shared row rendering ───────────────────────────────────────────────── */
    /** The three things a row can say, as dropdown items. `base` is what the row
     *  falls back to when nothing is authored — the role's answer when one is
     *  assigned, otherwise the org role's baseline. */
    items(memberDefault, base) {
        return [
            { value: 'default', label: this.defaultLabel(memberDefault, base), icon: 'circle-help' },
            { value: 'granted', label: t('perm.granted'), icon: 'check' },
            { value: 'revoked', label: t('perm.none'), icon: 'ban' },
        ];
    }
    defaultLabel(memberDefault, base) {
        if (base !== undefined) {
            return t('perm.overrideOf', { value: base ? t('perm.granted') : t('perm.none') });
        }
        return memberDefault ? t('perm.defaultAllowed') : t('perm.defaultDenied');
    }
    /** The text on a row's trigger — ALWAYS the current value, never a bare icon.
     *  This screen exists to answer "what may this person do"; a control that hides
     *  its own value makes the answer a click away, per row. */
    pillFor(choice, memberDefault, base) {
        switch (choice) {
            case 'default':
                return { label: this.defaultLabel(memberDefault, base), icon: 'circle-help' };
            case 'granted':
                return { label: t('perm.granted'), icon: 'check' };
            default:
                return { label: t('perm.none'), icon: 'ban' };
        }
    }
    /*
     * THE CATALOG, BUCKETED BY GROUP.
     *
     * It arrives in CONTRACT order — every group's `view` first, then every
     * group's `write` — so walking it and emitting a header on change produced a
     * header per ROW: eight sections became fifty, each holding one line, and the
     * shape of the permission set was invisible. Bucketing is what makes "GATEWAY"
     * mean the six things you can do to a gateway.
     */
    grouped() {
        const q = this.filter.trim().toLowerCase();
        const order = [];
        const byGroup = new Map();
        for (const c of CATALOG) {
            const key = c.key.replace(/^org:/, '');
            if (q &&
                !key.includes(q) &&
                !c.group.includes(q) &&
                !c.action.includes(q) &&
                // Searching by what is on screen, not only by the key. A reader working
                // in French types "verrouiller", never "symbol:lock".
                !permLabel(key).toLowerCase().includes(q) &&
                !permDesc(key).toLowerCase().includes(q) &&
                !permGroupLabel(c.group).toLowerCase().includes(q))
                continue;
            if (!byGroup.has(c.group)) {
                byGroup.set(c.group, []);
                order.push(c.group);
            }
            for (const entry of this.expand(key, c))
                byGroup.get(c.group).push(entry);
        }
        return order.map((group) => ({ group, entries: byGroup.get(group) }));
    }
    /**
     * One catalog entry becomes one row — unless the permission is INSTANCE-SCOPED,
     * in which case it becomes the wildcard row plus one row per instance.
     *
     * The instances are org-authored (a specialist invented this afternoon), so they
     * cannot come from the catalog; they arrive as data and are expanded here. The
     * wildcard is emitted FIRST and stays even when there are no instances, because
     * it is what every grant written before scoping existed actually says, and a
     * screen that stopped showing it would look like those grants had been lost.
     */
    expand(key, c) {
        const base = { key, action: c.action, memberDefault: c.memberDefault };
        const spec = specForKey(key);
        if (!spec?.instanceScoped || !this.specialists.length)
            return [base];
        return [
            base,
            ...this.specialists.map((s) => ({
                ...base,
                key: instanceKey(key, s.id),
                instanceOf: key,
                instanceName: s.name || s.id,
            })),
        ];
    }
    /**
     * The catalog as collapsible sections.
     *
     * `base` supplies the inherited value per key — set on the People tab when a
     * role is assigned, so a row reads "From role: All surfaces" rather than the
     * generic default, which would misreport it on exactly the members a role
     * covers.
     */
    rows(draft, onSet, base) {
        const groups = this.grouped();
        if (!groups.length)
            return [html `<p class="base-field-hint">${t('perm.noMatches')}</p>`];
        // A search is a request to SEE the matches, so it expands everything it
        // matched; a section the user opened by hand stays open on its own.
        const searching = this.filter.trim().length > 0;
        /*
         * ONE LIST, GROUPS AS SIBLINGS — not a bubble per group.
         *
         * Each group drew its own bordered box with a gap under it, so eleven groups
         * read as eleven separate panels that happened to be stacked, and the eye had
         * to re-establish "this is still the same list" at every gap. The tool policy
         * puts every group and every child in ONE box as consecutive rows, which is
         * what makes it scannable, and it is the same data shape here.
         *
         * The section wrapper stays because a group owns its children (collapsing it
         * removes them), but it contributes no chrome — the rows are the structure.
         */
        return groups.map(({ group, entries }) => {
            const changed = entries.filter((e) => draft[e.key] !== undefined).length;
            const open = searching || this.openGroups.has(group);
            /*
             * THE SAME INVENTORY LIST THE TOOL POLICY USES.
             *
             * Both screens do one job — scan a set of things, set a value on each — and
             * were drawn two different ways, so they did not read as relatives. A group
             * is a header row; a permission is a child row; the control sits on the
             * right of both. The shape is shared (`.base-inventory-*` in base-ui), so
             * neither app owns it and roles can adopt it without a third drawing.
             */
            return html `<div class="rbac-section">
        <button
          class="base-inventory-row base-inventory-head"
          aria-expanded=${open ? 'true' : 'false'}
          @click=${() => this.toggleGroup(group)}
        >
          <span class="base-inventory-ident">
            <vitesse-icon class="base-inventory-chevron ${open ? 'open' : ''}" name="chevron-right" size="14"></vitesse-icon>
            <span class="base-inventory-titles">
              <span class="base-inventory-name">${permGroupLabel(group)}</span>
              <span class="base-inventory-sub">
                ${changed
                ? t('perm.groupChanged', { changed, count: entries.length })
                : t('perm.groupCount', { count: entries.length })}
              </span>
            </span>
          </span>
        </button>
        ${open
                ? entries
                    .filter((e) => !e.instanceOf || searching || this.openPerms.has(e.instanceOf))
                    .map((e) => this.permissionRow(e, draft, onSet, base))
                : nothing}
      </div>`;
        });
    }
    toggleGroup(group) {
        const next = new Set(this.openGroups);
        if (next.has(group))
            next.delete(group);
        else
            next.add(group);
        this.openGroups = next;
    }
    /** One permission, as the same `.base-field` row the rest of the platform uses
     *  for "a labelled thing with a control beside it". */
    permissionRow(e, draft, onSet, base) {
        // A non-delegatable permission is never grantable through this screen — the
        // server drops it on read AND rejects it on write, so an editable-looking
        // control here could only ever produce a refusal or a silent no-op.
        const locked = NON_DELEGATABLE.has(e.key);
        const value = choiceFor(draft, e.key);
        // An instance row's DEFAULT is whatever the wildcard says, not the role
        // catalog's — so an unset row reads "From every specialist: ..." and matches
        // what the server would actually conclude for it.
        const inherited = e.instanceOf ? (draft[e.instanceOf] ?? base?.[e.instanceOf]) : base?.[e.key];
        const pill = this.pillFor(value, e.memberDefault, inherited);
        // The non-delegatable status is WORDS IN THE DESCRIPTION, not a second badge.
        /*
         * ONE SENTENCE, NO CHIPS.
         *
         * There were two badges competing on this row, and the reader had to work out
         * which one decided the control. Then the audience hint was a badge of its own,
         * which reads as a STATE — something the row is — when it is only guidance
         * about who usually holds it. The actual decision is in the dropdown beside it.
         *
         * So both fold into the description, in the order a person reads: what it lets
         * someone do, then who normally has it, then the constraint if there is one.
         */
        const audience = e.memberDefault ? t('perm.hintMemberDefault') : t('perm.hintAdminOnly');
        /*
         * AN INSTANCE ROW SAYS WHAT IT NARROWS.
         *
         * Left to the generic copy, twelve rows would carry the same sentence and the
         * name would be the only thing telling them apart — and nothing would say
         * that setting one of them overrides the wildcard above. The precedence is
         * the part a reader cannot guess, so it is stated on the row that depends on
         * it rather than in a note at the top nobody reads twice.
         */
        const label = e.instanceOf ? e.instanceName : permLabel(e.key);
        const desc = e.instanceOf
            ? `${t('perm.instanceOf', { permission: permLabel(e.instanceOf) })}. ${t('perm.instanceOverrides')}.`
            : locked
                ? `${permDesc(e.key)}. ${audience}. ${t('perm.lockedNote')}`
                : `${permDesc(e.key)}. ${audience}.`;
        /*
         * A SCOPED PARENT OWNS ITS INSTANCES, so it gets the same chevron a group
         * has: without it the specialists were always unfurled, and a permission
         * with a dozen of them buried the eleven ordinary rows around it. Only the
         * chevron toggles — clicking the row would fight the control on its right.
         */
        const parentOf = !e.instanceOf && this.specialists.length && specForKey(e.key)?.instanceScoped ? e.key : '';
        const openKids = parentOf ? this.filter.trim().length > 0 || this.openPerms.has(parentOf) : false;
        return html `<div class="base-inventory-row base-inventory-depth-${e.instanceOf ? 2 : 1}">
      ${parentOf
            ? /*
               * THE WHOLE IDENT IS THE CONTROL, exactly as a group header's is. Styling
               * the name to look hoverable while only the chevron answered a click is
               * the mismatch people notice by clicking the word and nothing happening —
               * so the name is INSIDE the button rather than beside it, and it lights up
               * because it is part of the thing being hovered.
               */
                html `<button
            class="base-inventory-ident base-inventory-head rbac-perm-toggle"
            aria-expanded=${openKids ? 'true' : 'false'}
            title=${t('perm.instanceToggle', { count: this.specialists.length })}
            @click=${() => this.togglePerm(parentOf)}
          >
            <vitesse-icon class="base-inventory-chevron ${openKids ? 'open' : ''}" name="chevron-right" size="14"></vitesse-icon>
            <span class="base-inventory-titles">
              <span class="base-inventory-name">${label}</span>
              <span class="base-inventory-sub" title=${desc}>${desc}</span>
            </span>
          </button>`
            : nothing}
      <span class="base-inventory-ident" ?hidden=${!!parentOf}>
        <span class="base-inventory-titles">
          <span class="base-inventory-name">${label}</span>
          <!-- The full sentence is the TITLE as well as the text: the row clamps to
               one line to stay scannable, so hover is how a long description stays
               reachable rather than being lost to the ellipsis. -->
          <span class="base-inventory-sub" title=${desc}>${desc}</span>
        </span>
      </span>
      <div class="base-inventory-control">
        ${locked
            ? html `<vitesse-icon name="shield-user" size="14"></vitesse-icon>`
            : /*
               * THE SAME <vitesse-select> THE TOOL POLICY USES for its per-tool tier.
               * Two screens that both answer "set one value per row" were reaching for
               * two different controls, so the same decision looked like two kinds of
               * decision. The row keeps showing its current value on the trigger —
               * this screen exists to answer "what may this person do", and a control
               * that hides its own value puts the answer a click away, per row.
               */
                html `<vitesse-select
              class="rbac-choice"
              title=${e.key}
              .options=${this.items(e.memberDefault, inherited).map((i) => ({ value: i.value, label: i.label }))}
              .value=${value}
              @change=${(ev) => onSet(e.key, ev.detail.value)}
            ></vitesse-select>`}
      </div>
    </div>`;
    }
    saveButton(dirty, run, allowed = this.canEdit) {
        if (!dirty || !allowed)
            return nothing;
        return html `<button class="base-button base-button-blue base-button-sm" ?disabled=${this.saving} @click=${run}>
      ${this.saving ? t('perm.saving') : t('perm.save')}
    </button>`;
    }
    togglePerm(key) {
        const next = new Set(this.openPerms);
        if (next.has(key))
            next.delete(key);
        else
            next.add(key);
        this.openPerms = next;
    }
    filterBox() {
        /*
         * THE SAME ROW THE LIST PAGES USE, wrapper included. The filter used to be a
         * bare `.base-search-field` while the lists put theirs inside `.members-toolbar`,
         * and two different shapes cannot be made to land on the same line by tuning
         * numbers — the margins that collapse around them differ. Identical markup is
         * what makes the alignment hold, rather than a nudge that is correct until
         * somebody edits either page.
         */
        return html `<div class="members-toolbar"><label class="base-search-field">
      <input
        .value=${this.filter}
        placeholder=${t('perm.filter')}
        @input=${(e) => (this.filter = e.target.value)}
      />
      <vitesse-icon class="base-search-icon" name="search" size="14"></vitesse-icon>
    </label></div>`;
    }
    /*
     * THE ROSTER IS THE SCREEN, and both roles are set ON the row.
     *
     * It used to be a cloud of chips over "Select a member", so giving five people
     * the same access meant five trips through a selection step and a wall of
     * fifty-nine dropdowns — for a change that is one field. The list puts the
     * question (who) and both answers (org role, permission profile) on one line,
     * and the wall becomes an "Edit" for the exception case it actually is.
     */
    peopleView() {
        if (!this.members.length)
            return html `<p class="base-field-hint">${t('perm.noMembers')}</p>`;
        if (this.selected)
            return this.memberEditor();
        const q = this.memberFilter.trim().toLowerCase();
        const shown = q ? this.members.filter((m) => m.name.toLowerCase().includes(q)) : this.members;
        return html `
      <p class="base-field-hint org-page-hint">${t('perm.oneRole')}</p>
      <div class="members-toolbar">
        <label class="base-search-field">
          <input
            .value=${this.memberFilter}
            placeholder=${t('perm.searchMembers')}
            @input=${(e) => (this.memberFilter = e.target.value)}
          />
          <vitesse-icon class="base-search-icon" name="search" size="14"></vitesse-icon>
        </label>
      </div>
      ${this.error ? html `<p class="rbac-err">${this.error}</p>` : nothing}
      <!-- The heading exists here for GEOMETRY as much as for meaning: Roles has
           two sections and therefore two headings, so a People tab with none
           started its list one heading higher and the first row jumped on every
           tab switch. It also says something the tab does not — the tab is the
           view, this names what is in the list. -->
      <p class="base-field-hint base-list-heading">${t('perm.everyone')}</p>
      <div class="base-roster-list base-roster-list--page">${shown.map((m) => this.memberRow(m))}</div>
    `;
    }
    /** Their face, or their initials when the picture does not arrive. Shared with
     *  the members roster so one person is never a photo on one tab and a blank
     *  disc on the next. */
    avatar(m) {
        return memberAvatar(m.name, m.imageUrl, () => this.requestUpdate());
    }
    memberRow(m) {
        const overrides = Object.keys(m.assignedRole ? m.overrides : m.grants).length;
        return html `<div class="base-roster-row">
      ${this.avatar(m)}
      <span class="rbac-who">
        <span class="rbac-name-text">${m.name}</span>
        ${m.email ? html `<span class="base-field-hint">${m.email}</span>` : nothing}
      </span>
      <span class="rbac-row-actions">
        ${overrides ? chip(t('perm.changes', { count: overrides })) : nothing}
        <vitesse-select
          size="md"
          class="rbac-role-select"
          title=${t('perm.colRole')}
          .options=${this.roleItems().map((i) => ({ value: i.value, label: i.label }))}
          .value=${this.roleChoice(m)}
          @change=${(e) => void this.setRoleChoice(m, e.detail.value)}
        ></vitesse-select>
        <button class="base-button base-button-neutral" @click=${() => this.select(m)}>
          ${t('perm.edit')}
        </button>
      </span>
    </div>`;
    }
    /** Which entry in the one list this member currently is. A profile wins over
     *  the baseline, because the profile's matrix is what actually gets enforced. */
    roleChoice(m) {
        if (m.assignedRole)
            return m.assignedRole;
        return m.role === 'admin' ? BUILT_IN_ADMIN : BUILT_IN_MEMBER;
    }
    roleItems() {
        return [
            { value: BUILT_IN_ADMIN, label: t('perm.orgAdmin'), icon: 'shield-user' },
            { value: BUILT_IN_MEMBER, label: t('perm.orgMember'), icon: 'user' },
            ...this.roles.map((r) => ({ value: r.name, label: r.name, icon: 'user-check' })),
        ];
    }
    /**
     * ONE choice, written to whichever stores it implies.
     *
     * Both writes are needed when the choice crosses the built-in boundary, and
     * they go to different systems (the IdP owns membership, fleet-control owns the
     * matrix), so this is the one place that knows the mapping. Optimistic, with a
     * full rollback: a failed write that leaves the new label on screen is worse
     * than an error, because the admin believes access changed and it did not.
     */
    async setRoleChoice(m, choice) {
        if (choice === this.roleChoice(m))
            return;
        const builtIn = choice === BUILT_IN_ADMIN || choice === BUILT_IN_MEMBER;
        const orgRole = choice === BUILT_IN_ADMIN ? 'admin' : 'member';
        const profile = builtIn ? '' : choice;
        const before = { role: m.role, assignedRole: m.assignedRole, overrides: m.overrides, grants: m.grants };
        this.error = '';
        m.role = orgRole;
        m.assignedRole = profile;
        // Overrides are deltas on the PREVIOUS role and mean nothing against the new
        // one; carrying them over would grant access nobody chose.
        m.overrides = {};
        m.grants = profile ? flatten(this.roles.find((r) => r.name === profile), {}) : {};
        this.requestUpdate();
        try {
            if (before.role !== orgRole) {
                if (!this.setOrgRole)
                    throw new Error('this surface cannot change org roles');
                await this.setOrgRole(m.userId, orgRole);
            }
            if (this.save && (before.assignedRole !== profile || Object.keys(before.overrides).length)) {
                await this.save(m.userId, { role: profile, overrides: {}, grants: {} });
            }
        }
        catch (e) {
            Object.assign(m, before);
            this.error = e instanceof Error ? e.message : String(e);
            this.requestUpdate();
        }
    }
    /** The exception case: per-permission deltas for ONE member. */
    memberEditor() {
        const current = this.members.find((m) => m.userId === this.selected);
        if (!current)
            return html `<p class="base-field-hint">${t('perm.pick')}</p>`;
        const role = this.assigned;
        return html `
      <!-- The same bar the role editor uses, for the same reason: the person's name
           reads as the heading at the top-left (theirs is not editable), the role they
           hold sits beside it because it is the one control this page has besides the
           permissions themselves, and Cancel/Save close the pair on the right. The
           filter then lands where the roster's search sits, so stepping into a person
           does not move the rows under it. -->
      <div class="rbac-bar rbac-bar--head">
        <span class="rbac-name rbac-name-inline rbac-name-static" title=${current.name}>${current.name}</span>
        <vitesse-select
          size="sm"
          class="rbac-role-select"
          title=${t('perm.roleLabel')}
          .options=${this.roleItems().map((i) => ({ value: i.value, label: i.label }))}
          .value=${this.roleChoice(current)}
          @change=${(e) => void this.setRoleChoice(current, e.detail.value)}
        ></vitesse-select>
        <span class="rbac-spacer"></span>
        ${this.error ? html `<span class="rbac-err">${this.error}</span>` : nothing}
        <button class="base-button base-button-neutral base-button-sm" @click=${() => (this.selected = '')}>
          ${t('common.cancel')}
        </button>
        ${this.saveButton(this.dirty, () => void this.persist())}
      </div>
      ${this.filterBox()}
      <div class="base-inventory-list rbac-perm-list">
        ${this.rows(this.draft, (k, c) => this.setRow(k, c), role?.grants)}
      </div>
    `;
    }
    /** How many members hold a role — the number that says whether renaming it is
     *  a typo fix or a change to five people's access. */
    holders(name) {
        return this.members.filter((m) => m.assignedRole === name).length;
    }
    /** One role, as an inventory row: identity left, what it costs and what you can
     *  do about it on the right. Shared by both sections so a Vitesse role and an
     *  org's own role differ only where they genuinely differ. */
    roleRow(opts) {
        // No avatar circle: a role is not a person, and a silhouette-in-a-circle beside every role read
        // as a blank headshot that carried no information the name did not. The name IS the identity.
        return html `<div class="base-inventory-row">
      <span class="base-inventory-ident">
        <span class="base-inventory-titles">
          <span class="base-inventory-name">${opts.name}</span>
          <span class="base-inventory-sub" title=${opts.hint}>${opts.hint}</span>
        </span>
      </span>
      <span class="base-inventory-control">
        ${opts.forked ? chip(t('perm.forked')) : nothing}
        <!-- No permission COUNT on a role row. A bare number does not tell anyone
             whether the role is right - two roles with 11 permissions each can differ
             completely - so it read as precision without information, and it competed
             with the one control on the row that does something. The counts inside the
             editor, which say WHICH group changed, are the useful ones. -->
        ${opts.onEdit
            ? html `<button class="base-button base-button-neutral" @click=${opts.onEdit}>
              ${t('perm.edit')}
            </button>`
            : nothing}
        ${opts.onDelete
            ? html `<button
              class="base-button base-button-red base-button-icon-only"
              title=${t('perm.deleteRole')}
              aria-label=${t('perm.deleteRole')}
              @click=${opts.onDelete}
            >
              <vitesse-icon name="trash" size="14"></vitesse-icon>
            </button>`
            : nothing}
      </span>
    </div>`;
    }
    rolesView() {
        if (this.roleSel)
            return this.roleEditor();
        /*
         * TWO SECTIONS, NO "BUILT IN" LABEL.
         *
         * There is no useful distinction between a role Vitesse compiled in and one a
         * super-admin authored — both are Vitesse's, both arrive the same way, and a
         * per-row "Built in" tag made the reader classify each row instead of reading
         * the list. What actually matters is WHOSE role it is: Vitesse's, which you
         * inherit and may customise, or your org's, which you own. So that is the
         * split, and it is a heading rather than a badge.
         *
         * Admin and Member sit in the Vitesse section too. They are org roles rather
         * than authored ones, so they carry no grant count and nothing to edit — but
         * they are still not the org's invention, and putting them anywhere else would
         * reintroduce the third category this removes.
         */
        const q = this.roleFilter.trim().toLowerCase();
        const match = (name) => !q || name.toLowerCase().includes(q);
        const upstream = this.roleDraft.filter((r) => r.upstream && match(r.name));
        const own = this.roleDraft.filter((r) => !r.upstream && match(r.name));
        return html `
      <p class="base-field-hint org-page-hint">${t('perm.rolesHint')}</p>
      <!--
        THE SAME TOOLBAR ROW as People and Members, in the same place.
        Switching tabs moved the first list up and down, because one tab had a
        search row and the other had none, and the create action sat at the BOTTOM
        of this one while its counterparts sat top-right. Same row, same height,
        same corner — so the eye stays where it was and only the list changes.
      -->
      <div class="members-toolbar">
        <label class="base-search-field">
          <input
            .value=${this.roleFilter}
            placeholder=${t('perm.searchRoles')}
            @input=${(e) => (this.roleFilter = e.target.value)}
          />
          <vitesse-icon class="base-search-icon" name="search" size="14"></vitesse-icon>
        </label>
        ${this.authorRoles
            ? html `<button class="base-button base-button-neutral base-button-sm" @click=${() => this.addRole()}>
              <vitesse-icon name="plus" size="14"></vitesse-icon>${t('perm.addRole')}
            </button>`
            : nothing}
      </div>
      ${this.error ? html `<p class="rbac-err">${this.error}</p>` : nothing}

      <p class="base-field-hint rbac-roles-heading">${t('perm.vitesseRoles')}</p>
      <div class="base-inventory-list">
        ${match(t('perm.orgAdmin'))
            ? this.roleRow({ name: t('perm.orgAdmin'), icon: 'shield-user', hint: t('perm.adminHint') })
            : nothing}
        ${match(t('perm.orgMember'))
            ? this.roleRow({
                name: t('perm.orgMember'),
                icon: 'user',
                hint: t('perm.memberHint'),
                // Dynamic: how many of the catalog's permissions a plain member holds by default —
                // the count moves on its own if a permission's member-default changes in the contract.
            })
            : nothing}
        ${upstream.map((r) => this.roleRow({
            name: r.name,
            icon: 'sparkles',
            hint: roleHint(r.name),
            forked: r.forked,
            onEdit: () => this.openRole(r.name),
        }))}
      </div>

      <p class="base-field-hint rbac-roles-heading">${t('perm.orgRoles')}</p>
      ${own.length
            ? html `<div class="base-inventory-list">
            ${own.map((r) => this.roleRow({
                name: r.name,
                icon: 'user-check',
                hint: this.holders(r.name) ? t('perm.usedBy', { count: this.holders(r.name) }) : t('perm.usedByNone'),
                onEdit: () => this.openRole(r.name),
                // Only when this person may author roles at all — the same gate the editor's own
                // delete carries, so the affordance and the permission cannot disagree.
                ...(this.authorRoles ? { onDelete: () => this.removeRole(r.name) } : {}),
            }))}
          </div>`
            : html `<p class="base-field-hint">${t('perm.noRoles')}</p>`}
      <!-- Save only. The create action moved into the toolbar above, beside the
           search, where every other page in this dialog puts its one action. -->
      <div class="rbac-bar">
        <span class="rbac-spacer"></span>
        ${this.saveButton(this.roleDirty, () => void this.persistRoles(), this.authorRoles)}
      </div>
    `;
    }
    openRole(name) {
        this.roleSel = name;
        this.error = '';
        // COLLAPSED, like every other view of this list. Opening a role used to expand
        // every section on the grounds that its grants are what an admin came to read,
        // but the result is the same wall of rows the collapse exists to avoid — and it
        // made the same screen behave two different ways depending on how you reached
        // it. A search still expands whatever it matched.
        this.openGroups = new Set();
        this.openPerms = new Set();
    }
    roleEditor() {
        const role = this.roleBeingEdited;
        if (!role)
            return html `<p class="base-field-hint">${t('perm.pickRole')}</p>`;
        /*
         * ONE BAR, THEN THE SAME FILTER ROW THE LIST HAS.
         *
         * This page used to open with a `Roles` back button, then spend a full
         * inventory row on a `Role name` field, and only then reach the permission
         * filter — so stepping into a role pushed the filter down the screen and
         * every section under it moved. Switching between the two pages read as two
         * different screens rather than one screen going deeper.
         *
         * The name IS the heading now, editable in place at the top-left and diffed by
         * the same Save the permissions use; Cancel is what returns you, so the back
         * button has nothing left to do. The filter then lands exactly where the list's
         * search sits, and nothing below it moves.
         */
        return html `
      <div class="rbac-bar rbac-bar--head">
        <input
          class="rbac-name rbac-name-inline"
          .value=${role.name}
          ?disabled=${!this.authorRoles}
          aria-label=${t('perm.roleName')}
          title=${t('perm.roleNameHint')}
          @change=${(e) => this.renameRole(e.target.value)}
        />
        <span class="rbac-spacer"></span>
        ${this.error ? html `<span class="rbac-err">${this.error}</span>` : nothing}
        <button class="base-button base-button-neutral base-button-sm" @click=${() => this.enterRoles()}>
          ${t('common.cancel')}
        </button>
        ${this.authorRoles
            ? html `<button class="base-button base-button-red base-button-sm" @click=${() => this.deleteRole()}>
              ${t('perm.deleteRole')}
            </button>`
            : nothing}
        ${this.saveButton(this.roleDirty, () => void this.persistRoles(), this.authorRoles)}
      </div>
      ${this.filterBox()}
      <div class="base-inventory-list rbac-perm-list">
        ${this.rows(role.grants, (k, c) => this.setRoleRow(k, c))}
      </div>
    `;
    }
    /** One tab of the strip, identical in shape to the Members page's. */
    rbacTab(id, label) {
        return html `<button
      class="base-tab"
      role="tab"
      aria-selected=${this.tab === id ? 'true' : 'false'}
      @click=${() => {
            this.error = '';
            if (id === 'roles')
                this.enterRoles();
            else
                this.tab = 'people';
        }}
    >
      ${label}
    </button>`;
    }
    /*
     * TIER GATING — RBAC is a graduated capability (none < profiles < per_user).
     *   · Below `profiles` (the free/standard floor): the WHOLE panel is locked — the
     *     outer gate in render() blurs it and offers Upgrade.
     *   · At `profiles` (pro): the panel opens to the roles, but per-USER control —
     *     editing an individual's rows, and authoring custom roles — needs `per_user`.
     *   · At `per_user` (ultra): everything.
     * Fail-OPEN on a missing session, exactly like <vitesse-gate-overlay>, so a wiring
     * gap never hides a paying admin's controls.
     */
    allowsPerUser() {
        const s = getSession();
        return !s || s.allows({ capability: 'rbac', level: 'per_user' });
    }
    /** Authoring roles (create / edit / delete / rename) is admin-gated AND tier-gated:
     *  a pro admin can read the roles but not author them; that needs `per_user`. */
    get authorRoles() {
        return this.canEditRoles && this.allowsPerUser();
    }
    render() {
        /*
          THE SAME TAB STRIP as the Members page next door — underline, count, one
          placement. They are two pages of one dialog answering two halves of one
          question. Counts included so the number is the first thing you want from a
          tab you are not on.
    
          The People view is per-USER control, so at the `profiles` tier (pro) it is
          itself gated at `per_user`: the reader sees its shape blurred with an Upgrade,
          never the roster, while the Roles view beside it stays open.
        */
        const inner = html `
      <div class="base-tab-list" role="tablist">
        ${this.rbacTab('people', t('perm.tabPeople'))}
        ${this.rbacTab('roles', t('perm.tabRoles'))}
      </div>
      ${this.tab === 'people'
            ? html `<vitesse-gate-overlay capability="rbac" level="per_user" .content=${this.peopleView()}></vitesse-gate-overlay>`
            : this.rolesView()}
    `;
        // The whole panel is gated at `profiles`: below it (standard) there is no RBAC at all.
        return html `<vitesse-gate-overlay capability="rbac" level="profiles" .content=${inner}></vitesse-gate-overlay>`;
    }
};
__decorate([
    property({ attribute: false })
], VitesseRbacPanel.prototype, "members", void 0);
__decorate([
    property({ attribute: false })
], VitesseRbacPanel.prototype, "roles", void 0);
__decorate([
    property({ attribute: false })
], VitesseRbacPanel.prototype, "save", void 0);
__decorate([
    property({ attribute: false })
], VitesseRbacPanel.prototype, "setOrgRole", void 0);
__decorate([
    property({ attribute: false })
], VitesseRbacPanel.prototype, "saveRoles", void 0);
__decorate([
    property({ type: Boolean })
], VitesseRbacPanel.prototype, "canEdit", void 0);
__decorate([
    property({ type: Boolean })
], VitesseRbacPanel.prototype, "canEditRoles", void 0);
__decorate([
    property({ attribute: false })
], VitesseRbacPanel.prototype, "specialists", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "tab", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "selected", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "draft", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "draftRole", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "dirty", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "saving", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "error", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "filter", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "openGroups", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "openPerms", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "memberFilter", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "roleFilter", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "roleDraft", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "roleSel", void 0);
__decorate([
    state()
], VitesseRbacPanel.prototype, "roleDirty", void 0);
VitesseRbacPanel = __decorate([
    customElement('vitesse-rbac-panel')
], VitesseRbacPanel);
export { VitesseRbacPanel };
//# sourceMappingURL=vitesse-rbac-panel.js.map